"use client";

import { IconArrowLeft } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "@/components/ui/Button";
import { useIsMobile } from "@/features/layout/useIsMobile";
import { useMobileView } from "@/features/layout/useMobileView";
import { useShareModal } from "@/features/share/useShareUrl";
import { usePOIView } from "../../hooks/usePOIView";
import { useSelectedItem } from "../../hooks/useSelectedItem";
import { useSelectedItemId } from "../../hooks/useSelectedItemId";
import { POIPrintPreview } from "./POIPrintPreview";

/**
 * The POI panel's own toolbar: back, print, and share.
 *
 * "Back" means two different things. On mobile the panel is a full screen the
 * user arrived at from the list, so it returns there and leaves the POI selected.
 * On desktop the panel replaces the results beside the map, so going back means
 * deselecting.
 *
 * Share opens the app's share dialog rather than the legacy dropdown of the same
 * name: the URL already carries the selected POI, so it shares this page, and the
 * app has one share surface instead of two that could drift.
 */
export function POIHeader() {
  const t = useTranslations("poiPage");
  const isMobile = useIsMobile();
  const { previousView, setView } = useMobileView();
  const { setPOIView } = usePOIView();
  const { setSelectedItemId } = useSelectedItemId();
  const { open: openShareModal } = useShareModal();
  const { data: item } = useSelectedItem();

  const returnsToList = isMobile && previousView === "list";

  const onBack = () => {
    void setPOIView(false);
    if (returnsToList) void setView("list");
    else void setSelectedItemId(null);
  };

  const contentRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setPrinting] = useState(false);

  // `useReactToPrint` memoizes on its option values, so this has to keep its
  // identity: an inline arrow would give `print` a new one every render and the
  // effect below would fire on each of them.
  const stopPrinting = useCallback(() => setPrinting(false), []);
  const print = useReactToPrint({ contentRef, onAfterPrint: stopPrinting });

  // The preview is only mounted while printing — it loads full-size gallery
  // images, which would otherwise be fetched for every POI merely opened — so
  // printing has to wait for it to exist. Legacy guessed at 100ms; an effect
  // runs exactly once the node is committed.
  useEffect(() => {
    if (isPrinting) print();
  }, [isPrinting, print]);

  return (
    <div className="flex items-center justify-between p-3">
      <Button variant="ghost" onClick={onBack}>
        <IconArrowLeft size={18} aria-hidden />
        {returnsToList ? t("back_to") : t("back")}
      </Button>

      <div className="relative hidden items-center justify-end tablet:flex">
        <Button
          variant="ghost"
          size="icon-lg"
          onClick={() => setPrinting(true)}
          aria-label={t("popovers.print")}
        >
          <i className="icon ic-ios_Printer1 text-[14px]" />
        </Button>

        {isPrinting && item && (
          // Hidden on the wrapper, not on the printed node: react-to-print clones
          // `contentRef` into an iframe, so a `display: none` of its own would
          // travel with it and print a blank page.
          <div className="hidden">
            <div ref={contentRef}>
              <POIPrintPreview item={item} />
            </div>
          </div>
        )}

        <Button
          variant="ghost"
          size="icon-lg"
          onClick={openShareModal}
          aria-label={t("share")}
        >
          <i className="icon ic-fontonicons_ShareThis text-[14px]" />
        </Button>
      </div>
    </div>
  );
}
